import { Badge, Button, Surface, type SurfaceTone } from '../surface.js';
import type { PushState } from './push-state.js';

/**
 * The push-notification control: what state this device is in, and the ONE
 * next step that state allows. Composes Surface / Badge / Button so it looks
 * like the rest of whichever app hosts it; every word arrives via `copy` (this
 * package has no i18n), so the host app keeps its own voice and brand.
 *
 * Each state ends in an action or an instruction — never a dead end:
 *   on                → reassurance (what will arrive)
 *   ask               → "Turn on" button
 *   blocked           → where to re-enable it in browser / OS settings
 *   ios-needs-install → Share → "Add to Home Screen", then come back here
 *   unsupported       → which browsers will work
 */

export interface PushStateCopy {
  readonly status: string;
  readonly body: string;
}

export interface PushPermissionCardCopy {
  readonly title: string;
  readonly checking: string;
  readonly on: PushStateCopy;
  readonly ask: PushStateCopy & { readonly action: string; readonly busyAction: string };
  readonly blocked: PushStateCopy;
  readonly iosInstall: PushStateCopy;
  readonly unsupported: PushStateCopy;
  /** Shown when permission is granted but this device failed to register. */
  readonly registrationFailed: string;
  readonly retry: string;
}

export interface PushPermissionCardProps {
  readonly state: PushState;
  readonly busy: boolean;
  readonly registrationFailed: boolean;
  readonly onEnable: () => void;
  readonly copy: PushPermissionCardCopy;
  readonly as?: 'div' | 'section';
}

const TONE: Record<Exclude<PushState, 'checking'>, SurfaceTone> = {
  on: 'success',
  ask: 'info',
  blocked: 'warn',
  'ios-needs-install': 'info',
  unsupported: 'neutral',
};

export function PushPermissionCard({
  state,
  busy,
  registrationFailed,
  onEnable,
  copy,
  as = 'section',
}: PushPermissionCardProps) {
  const entry: PushStateCopy | null =
    state === 'on'
      ? copy.on
      : state === 'ask'
        ? copy.ask
        : state === 'blocked'
          ? copy.blocked
          : state === 'ios-needs-install'
            ? copy.iosInstall
            : state === 'unsupported'
              ? copy.unsupported
              : null;

  return (
    <Surface
      as={as}
      level={0}
      aria-busy={state === 'checking' || busy}
      style={{ display: 'grid', gap: 'var(--space-3)' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>{copy.title}</h2>
        {state !== 'checking' && entry ? <Badge tone={TONE[state]}>{entry.status}</Badge> : null}
      </div>
      <p
        role="status"
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
          overflowWrap: 'anywhere',
        }}
      >
        {entry ? entry.body : copy.checking}
      </p>
      {state === 'on' && registrationFailed ? (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-warn-bg)',
            fontSize: 'var(--text-meta)',
          }}
        >
          <span style={{ flex: '1 1 12rem' }}>{copy.registrationFailed}</span>
          <Button variant="secondary" onClick={onEnable} disabled={busy}>
            {copy.retry}
          </Button>
        </div>
      ) : null}
      {state === 'ask' ? (
        <div>
          <Button variant="primary" onClick={onEnable} disabled={busy} aria-disabled={busy}>
            {busy ? copy.ask.busyAction : copy.ask.action}
          </Button>
        </div>
      ) : null}
    </Surface>
  );
}
